import moment from 'moment'
import {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {DataCSRItem} from '../core/_models'
import {CustomRow} from './columns/CustomRow'
import {CSRColumns} from './columns/_columns'
import {CSRChart} from '../components/CSRChart'
import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {csrAPI} from '../../../services/CSRService'
import {KTCardBody} from '../../../template/helpers'
import {usersAPI} from '../../../services/UsersService'
import {useTable, ColumnInstance, Row} from 'react-table'
import {CustomHeaderColumn} from './columns/CustomHeaderColumn'
import {TableLoading} from '../../../template/helpers/TableLoading'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import clsx from 'clsx'
import {setQuery} from '../../cdr/slices/cdrFilterSlice'
import {shallowEqual} from 'react-redux'
import {useAccount} from '../../../hooks/useAccount'

const CSRTable = () => {
  const {appId} = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()
  const query = useAppSelector((state) => state.csrFilter)
  const queryCdr = useAppSelector((state) => state.cdrFilter.query, shallowEqual)
  const {currentAccountIdForRequest: accountId} = useAccount()
  const [fetch, {data, isLoading}] = csrAPI.useDataFetchMutation()
  const [linkCDR] = useState<string>(location.pathname.includes('/management') ? '/management/cdr' : '/cdr')

  useEffect(() => {
    fetch({id: appId!, filter: {...query}, account_id: accountId})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, accountId])

  const dataItems = data ? (data?.items as DataCSRItem[]) : []

  const columns = useMemo(() => CSRColumns, [])
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data: dataItems,
  })

  const formatPeriod = (period?: string) => {
    if (!period) return ''

    const tz = currentUser?.time_zone || moment.tz.guess()
    const [start, end] = period.split(',')

    const startLocal = moment.utc(start).tz(tz)
    const endLocal = moment.utc(end).tz(tz)

    const formatter = new Intl.DateTimeFormat(currentUser?.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })

    return `с ${formatter.format(startLocal.toDate())} по ${formatter.format(endLocal.toDate())}`
  }

  const total = dataItems.reduce((sum, i) => sum + i.total, 0)
  const totalinbound = dataItems.reduce((sum, i) => sum + i.inbound, 0)
  const totaloutbound = dataItems.reduce((sum, i) => sum + i.outbound, 0)
  const totalMissed = dataItems.reduce((sum, i) => sum + i.missed, 0)

  return (
    <KTCardBody className='pt-1 pb-4'>
      {isLoading && <TableLoading />}
      <CSRChart items={dataItems} />
      <div className='table-responsive'>
        <table
          id='kt_table_users'
          className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          {...getTableProps()}
        >
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              {headers.map((column: ColumnInstance<DataCSRItem>, i) => (
                <CustomHeaderColumn key={i} column={column} />
              ))}
            </tr>
          </thead>
          <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
            {rows.length > 0 && (
              <tr>
                <td>
                  <div className='d-flex align-content-center text-primary fw-bolder'>{formatPeriod(query.period)}</div>
                </td>
                <td>
                  {total > 0 ? (
                    <OverlayTrigger
                      delay={{show: 50, hide: 50}}
                      overlay={
                        <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                          Перейти к журналу звонков
                        </Tooltip>
                      }
                    >
                      <span
                        className={clsx('text-warning small', {'text-hover-primary cursor-pointer': total > 0})}
                        onClick={() => {
                          dispatch(
                            setQuery({
                              period: query.period,
                              limit: queryCdr?.limit,
                              offset: 0,
                            })
                          )
                          navigate(`${linkCDR}/${appId}/`)
                        }}
                      >
                        {total}
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <div className='d-flex align-content-center text-warning'>{total}</div>
                  )}
                </td>
                <td>
                  {totalinbound > 0 ? (
                    <OverlayTrigger
                      delay={{show: 50, hide: 50}}
                      overlay={
                        <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                          Перейти к <span className='text-primary'>входящим</span> в журнале звонков
                        </Tooltip>
                      }
                    >
                      <span
                        className={clsx('text-primary small', {'text-hover-primary cursor-pointer': totalinbound > 0})}
                        onClick={() => {
                          dispatch(
                            setQuery({
                              period: query.period,
                              limit: queryCdr?.limit,
                              offset: 0,
                              direction: 'INBOUND',
                            })
                          )
                          navigate(`${linkCDR}/${appId}/`)
                        }}
                      >
                        {totalinbound}
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <div className='d-flex align-content-center text-primary'>{totalinbound}</div>
                  )}
                </td>
                <td>
                  {totaloutbound > 0 ? (
                    <OverlayTrigger
                      delay={{show: 50, hide: 50}}
                      overlay={
                        <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                          Перейти к <span className='text-success'>исходящим</span> в журнале звонков
                        </Tooltip>
                      }
                    >
                      <span
                        className={clsx('text-success small', {'text-hover-primary cursor-pointer': totaloutbound > 0})}
                        onClick={() => {
                          dispatch(
                            setQuery({
                              period: query.period,
                              limit: queryCdr?.limit,
                              offset: 0,
                              direction: 'OUTBOUND',
                            })
                          )
                          navigate(`${linkCDR}/${appId}/`)
                        }}
                      >
                        {totaloutbound}
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <div className='d-flex align-content-center text-success'>{totaloutbound}</div>
                  )}
                </td>
                <td>
                  {totalMissed > 0 ? (
                    <OverlayTrigger
                      delay={{show: 50, hide: 50}}
                      overlay={
                        <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                          Перейти к <span className='text-danger'>пропущенным</span> в журнале звонков
                        </Tooltip>
                      }
                    >
                      <span
                        className={clsx('text-danger small', {'text-hover-primary cursor-pointer': totalMissed > 0})}
                        onClick={() => {
                          dispatch(
                            setQuery({
                              period: query.period,
                              limit: queryCdr?.limit,
                              offset: 0,
                              direction: 'INBOUND',
                              status: 'NO_ANSWER',
                            })
                          )
                          navigate(`${linkCDR}/${appId}/`)
                        }}
                      >
                        {totalMissed}
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <div className='d-flex align-content-center text-danger'>{totalMissed}</div>
                  )}
                </td>
              </tr>
            )}
            {rows.length > 0 ? (
              rows.map((row: Row<DataCSRItem>, i) => {
                prepareRow(row)
                return <CustomRow row={row} key={`row-${i}-${row.id}`} />
              })
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>---</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </KTCardBody>
  )
}

export {CSRTable}
